import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  GitCompare, ArrowLeft, Trash2, Plus, BedDouble, Bath, Square,
  MapPin, Star, Check, X as XIcon, Building2, CalendarClock,
} from "lucide-react";
import api from "../api";
import { useAppContext } from "../context/AppContext";
import { fmtPrice } from "../assets/data";
import LoadingSpinner from "../components/LoadingSpinner";

/* ── helpers ───────────────────────────────────────────────── */
function Val({ a, b, better, value }) {
  const diff = a !== b;
  const win  = diff && better;
  return (
    <div style={{
      padding: "1rem 1.25rem",
      background: win ? "rgba(201,168,76,.07)" : diff ? "rgba(248,113,113,.04)" : "transparent",
      borderLeft: win ? "3px solid var(--gold)" : diff ? "3px solid rgba(248,113,113,.3)" : "3px solid transparent",
      transition: "background .2s",
      fontSize: ".9rem",
      fontFamily: "var(--sans)",
      color: win ? "var(--navy)" : "var(--gray)",
      fontWeight: win ? 600 : 400,
      minHeight: 52,
      display: "flex",
      alignItems: "center",
    }}>
      {value ?? "—"}
    </div>
  );
}

function getRows(p, lang, cur) {
  if (!p) return Array(9).fill(null);
  return [
    fmtPrice(p, cur),
    `${p.area} ${lang === "en" ? "m²" : "م²"}`,
    `${p.rooms} ${lang === "en" ? (p.rooms === 1 ? "Bedroom" : "Bedrooms") : "غرف"}`,
    `${p.baths} ${lang === "en" ? (p.baths === 1 ? "Bathroom" : "Bathrooms") : "حمامات"}`,
    p.type,
    p.status,
    lang === "ar" ? (p.locationAr || p.location) : p.location,
    p.deliveryYear ? String(p.deliveryYear) : "—",
    p.rating ? `${p.rating} / 5` : "—",
  ];
}

const ROW_LABELS = {
  en: ["Price", "Area", "Bedrooms", "Bathrooms", "Type", "Status", "Location", "Delivery", "Rating"],
  ar: ["السعر", "المساحة", "غرف النوم", "الحمامات", "النوع", "الحالة", "الموقع", "التسليم", "التقييم"],
};

/* higher = better for these row indices */
const HIGHER_BETTER = [2, 3, 8]; // rooms, baths, rating
/* lower = better for price (index 0) */
const LOWER_BETTER  = [0];

function winnerFlags(a, b) {
  const vals = getRows(a, "en", "EGP");
  const vb   = getRows(b, "en", "EGP");
  return vals.map((va, i) => {
    if (!a || !b || va == null || vb[i] == null) return [false, false];
    const na = parseFloat(String(va).replace(/[^0-9.]/g, ""));
    const nb = parseFloat(String(vb[i]).replace(/[^0-9.]/g, ""));
    if (isNaN(na) || isNaN(nb) || na === nb) return [false, false];
    if (HIGHER_BETTER.includes(i)) return na > nb ? [true, false] : [false, true];
    if (LOWER_BETTER.includes(i))  return na < nb ? [true, false] : [false, true];
    return [false, false];
  });
}

/* ── Placeholder slot when no property selected ─────────────── */
function EmptySlot({ lang, onClick }) {
  return (
    <div
      onClick={onClick}
      style={{
        flex: 1,
        minWidth: 220,
        border: "2px dashed rgba(201,168,76,.3)",
        borderRadius: 14,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: ".75rem",
        padding: "3rem 1rem",
        cursor: "pointer",
        transition: "border-color .2s, background .2s",
        background: "transparent",
      }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = "var(--gold)"; e.currentTarget.style.background = "rgba(201,168,76,.04)"; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(201,168,76,.3)"; e.currentTarget.style.background = "transparent"; }}
    >
      <div style={{ width: 52, height: 52, borderRadius: "50%", border: "2px dashed rgba(201,168,76,.4)", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Plus size={22} color="var(--gold)" />
      </div>
      <div style={{ color: "var(--gray)", fontSize: ".82rem", fontFamily: "var(--sans)", textAlign: "center" }}>
        {lang === "en" ? "Add a property to compare" : "أضف عقارًا للمقارنة"}
      </div>
    </div>
  );
}

/* ── Property header card ───────────────────────────────────── */
function PropHeader({ comp, lang, onRemove, onNavigate }) {
  const p = comp.project;
  return (
    <div style={{ flex: 1, minWidth: 220, background: "#fff", borderRadius: 14, overflow: "hidden", border: "1px solid rgba(201,168,76,.12)", boxShadow: "0 4px 20px rgba(10,22,40,.06)" }}>
      <div style={{ position: "relative", height: 160 }}>
        <img
          src={p.img || "https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=700&q=80"}
          alt={p.name}
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
        <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to top,rgba(10,22,40,.7),transparent)" }} />
        {p.rating && (
          <div style={{ position: "absolute", top: 10, right: 10, background: "rgba(10,22,40,.8)", backdropFilter: "blur(8px)", borderRadius: 4, padding: "3px 8px", display: "flex", alignItems: "center", gap: 3 }}>
            <Star size={10} fill="var(--gold)" color="var(--gold)" />
            <span style={{ color: "var(--gold)", fontSize: ".7rem", fontWeight: 700 }}>{p.rating}</span>
          </div>
        )}
        <button
          onClick={() => onRemove(comp.projectId)}
          style={{ position: "absolute", top: 10, left: 10, width: 32, height: 32, borderRadius: "50%", background: "rgba(248,113,113,.9)", border: "none", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#fff", transition: "transform .2s" }}
          onMouseEnter={e => e.currentTarget.style.transform = "scale(1.1)"}
          onMouseLeave={e => e.currentTarget.style.transform = "scale(1)"}
          title={lang === "en" ? "Remove" : "إزالة"}
        >
          <Trash2 size={14} />
        </button>
      </div>
      <div style={{ padding: "1rem" }}>
        <h3 style={{ fontFamily: "var(--serif)", fontSize: "1.05rem", color: "var(--navy)", fontWeight: 500, marginBottom: ".35rem", lineHeight: 1.25 }}>
          {lang === "ar" ? p.nameAr : p.name}
        </h3>
        <div style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: ".85rem" }}>
          <MapPin size={11} color="var(--gold)" />
          <span style={{ fontSize: ".72rem", color: "var(--gray)", fontFamily: "var(--sans)" }}>
            {lang === "ar" ? (p.locationAr || p.location) : p.location}
          </span>
        </div>
        <button
          onClick={() => onNavigate(comp.projectId)}
          className="btn-g"
          style={{ width: "100%", borderRadius: 6, padding: "8px", fontSize: ".76rem" }}
        >
          {lang === "en" ? "View Details" : "عرض التفاصيل"}
        </button>
      </div>
    </div>
  );
}

/* ── Main page ──────────────────────────────────────────────── */
export default function ComparisonPage() {
  const { lang, cur, user } = useAppContext();
  const navigate = useNavigate();
  const [comparisons, setComparisons] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) { navigate("/login"); return; }
    api.get("/api/comparisons")
      .then(data => setComparisons(data.slice(0, 2)))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [user, navigate]);

  const remove = async (projectId) => {
    await api.delete(`/api/comparisons/${projectId}`).catch(() => {});
    setComparisons(prev => prev.filter(c => c.projectId !== projectId));
  };

  if (loading) return (
    <div style={{ minHeight: "100vh", background: "var(--cream)", paddingTop: 80, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <LoadingSpinner size={36} trackColor="rgba(201,168,76,.2)" />
    </div>
  );

  const a = comparisons[0] ?? null;
  const b = comparisons[1] ?? null;
  const pa = a?.project ?? null;
  const pb = b?.project ?? null;
  const rowsA  = getRows(pa, lang, cur);
  const rowsB  = getRows(pb, lang, cur);
  const wins   = pa && pb ? winnerFlags(pa, pb) : null;
  const labels = ROW_LABELS[lang];

  return (
    <div style={{ minHeight: "100vh", background: "var(--cream)", paddingTop: 80 }}>
      {/* Header */}
      <div style={{ background: "linear-gradient(135deg,var(--navy),var(--navy2))", padding: "3.5rem 5% 3rem" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", flexWrap: "wrap", gap: "1rem" }}>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: ".6rem", marginBottom: ".6rem" }}>
                <GitCompare size={16} color="var(--gold)" />
                <span style={{ fontSize: ".6rem", color: "var(--gold)", letterSpacing: ".22em", textTransform: "uppercase", fontFamily: "var(--sans)" }}>
                  {lang === "en" ? "Property Comparison" : "مقارنة العقارات"}
                </span>
              </div>
              <h1 style={{ fontFamily: "var(--serif)", fontSize: "clamp(1.8rem,4vw,2.6rem)", color: "#fff", fontWeight: 300, lineHeight: 1.1, marginBottom: ".4rem" }}>
                {lang === "en" ? "Compare Properties" : "قارن بين العقارات"}
              </h1>
              <p style={{ color: "rgba(255,255,255,.4)", fontSize: ".88rem", fontFamily: "var(--sans)" }}>
                {lang === "en"
                  ? `${comparisons.length} of 2 properties selected`
                  : `${comparisons.length} من 2 عقارات مختارة`}
              </p>
            </div>
            <div style={{ display: "flex", gap: ".7rem" }}>
              <button
                className="btn-o"
                onClick={() => navigate("/favorites")}
                style={{ borderRadius: 6, padding: "9px 18px", fontSize: ".8rem", display: "flex", alignItems: "center", gap: 6 }}
              >
                {lang === "en" ? "My Favorites" : "المفضلة"}
              </button>
              <button
                className="btn-g"
                onClick={() => navigate("/properties")}
                style={{ borderRadius: 6, padding: "9px 18px", fontSize: ".8rem", display: "flex", alignItems: "center", gap: 6 }}
              >
                <ArrowLeft size={14} />
                {lang === "en" ? "Browse Properties" : "تصفح العقارات"}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 1100, margin: "0 auto", padding: "3rem 5% 5rem" }}>

        {/* Empty state — no properties at all */}
        {comparisons.length === 0 ? (
          <div style={{ textAlign: "center", padding: "6rem 1rem" }}>
            <div style={{ width: 80, height: 80, borderRadius: "50%", background: "rgba(201,168,76,.1)", border: "1px solid rgba(201,168,76,.2)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 1.5rem" }}>
              <GitCompare size={36} stroke="var(--gold)" strokeWidth={1.25} />
            </div>
            <h2 style={{ fontFamily: "var(--serif)", fontSize: "1.7rem", color: "var(--navy)", fontWeight: 300, marginBottom: ".5rem" }}>
              {lang === "en" ? "Nothing to compare yet" : "لا يوجد شيء للمقارنة بعد"}
            </h2>
            <p style={{ color: "var(--gray)", fontSize: ".9rem", marginBottom: "2rem" }}>
              {lang === "en"
                ? "Use the compare button on any property card to add it here."
                : "استخدم زر المقارنة على أي بطاقة عقار لإضافته هنا."}
            </p>
            <button className="btn-g" onClick={() => navigate("/properties")} style={{ borderRadius: 6, padding: "11px 28px", fontSize: ".86rem" }}>
              {lang === "en" ? "Browse Properties" : "تصفح العقارات"}
            </button>
          </div>
        ) : (
          <>
            {/* Property header cards */}
            <div style={{ display: "flex", gap: "1.5rem", marginBottom: "2.5rem", flexWrap: "wrap" }}>
              {a && <PropHeader comp={a} lang={lang} onRemove={remove} onNavigate={id => navigate(`/properties/${id}`)} />}
              {b ? (
                <PropHeader comp={b} lang={lang} onRemove={remove} onNavigate={id => navigate(`/properties/${id}`)} />
              ) : (
                <EmptySlot lang={lang} onClick={() => navigate("/properties")} />
              )}
            </div>

            {/* Comparison table */}
            {pa && (
              <div style={{ background: "#fff", borderRadius: 16, overflow: "hidden", border: "1px solid rgba(201,168,76,.12)", boxShadow: "0 4px 24px rgba(10,22,40,.06)" }}>
                {/* Column headers */}
                <div style={{ display: "grid", gridTemplateColumns: "180px 1fr 1fr", background: "var(--navy)", borderBottom: "1px solid rgba(201,168,76,.15)" }}>
                  <div style={{ padding: "1rem 1.25rem", fontSize: ".58rem", color: "rgba(255,255,255,.4)", letterSpacing: ".2em", textTransform: "uppercase", fontFamily: "var(--sans)" }}>
                    {lang === "en" ? "Attribute" : "الخاصية"}
                  </div>
                  {[a, b ?? null].map((comp, ci) => (
                    <div key={ci} style={{ padding: "1rem 1.25rem", fontFamily: "var(--serif)", color: "#fff", fontSize: ".95rem", fontWeight: 400 }}>
                      {comp ? (lang === "ar" ? comp.project.nameAr : comp.project.name) : (
                        <span style={{ color: "rgba(255,255,255,.3)", fontSize: ".82rem" }}>
                          {lang === "en" ? "— Not selected —" : "— غير مختار —"}
                        </span>
                      )}
                    </div>
                  ))}
                </div>

                {/* Rows */}
                {labels.map((label, i) => (
                  <div
                    key={i}
                    style={{
                      display: "grid",
                      gridTemplateColumns: "180px 1fr 1fr",
                      borderBottom: i < labels.length - 1 ? "1px solid rgba(0,0,0,.05)" : "none",
                    }}
                  >
                    {/* Label */}
                    <div style={{
                      padding: "1rem 1.25rem",
                      fontSize: ".72rem",
                      color: "var(--navy)",
                      fontWeight: 700,
                      letterSpacing: ".06em",
                      textTransform: "uppercase",
                      fontFamily: "var(--sans)",
                      background: "rgba(201,168,76,.04)",
                      display: "flex",
                      alignItems: "center",
                    }}>
                      {label}
                    </div>

                    {/* Value A */}
                    <Val
                      a={rowsA[i]}
                      b={rowsB[i]}
                      better={wins ? wins[i][0] : false}
                      value={rowsA[i]}
                    />

                    {/* Value B */}
                    {pb ? (
                      <Val
                        a={rowsA[i]}
                        b={rowsB[i]}
                        better={wins ? wins[i][1] : false}
                        value={rowsB[i]}
                      />
                    ) : (
                      <div style={{ padding: "1rem 1.25rem", color: "rgba(0,0,0,.18)", fontSize: ".82rem", fontFamily: "var(--sans)", display: "flex", alignItems: "center" }}>—</div>
                    )}
                  </div>
                ))}

                {/* Amenities row */}
                {pa.amenities?.length > 0 && (
                  <div style={{ display: "grid", gridTemplateColumns: "180px 1fr 1fr", borderTop: "1px solid rgba(0,0,0,.05)" }}>
                    <div style={{ padding: "1rem 1.25rem", fontSize: ".72rem", color: "var(--navy)", fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase", fontFamily: "var(--sans)", background: "rgba(201,168,76,.04)", display: "flex", alignItems: "center" }}>
                      {lang === "en" ? "Amenities" : "المميزات"}
                    </div>
                    {[pa, pb].map((p, ci) => (
                      <div key={ci} style={{ padding: "1rem 1.25rem" }}>
                        {p ? (
                          <div style={{ display: "flex", flexWrap: "wrap", gap: ".35rem" }}>
                            {(p.amenities || []).map(am => (
                              <span key={am} style={{ background: "rgba(201,168,76,.1)", border: "1px solid rgba(201,168,76,.2)", borderRadius: 20, padding: "2px 9px", fontSize: ".68rem", color: "var(--navy)", fontFamily: "var(--sans)" }}>{am}</span>
                            ))}
                          </div>
                        ) : (
                          <span style={{ color: "rgba(0,0,0,.18)", fontSize: ".82rem" }}>—</span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Legend */}
            {pa && pb && (
              <div style={{ display: "flex", gap: "1.5rem", marginTop: "1.5rem", flexWrap: "wrap" }}>
                {[
                  { color: "var(--gold)", bg: "rgba(201,168,76,.1)", label: lang === "en" ? "Better value" : "قيمة أفضل" },
                  { color: "#ef4444", bg: "rgba(248,113,113,.08)", label: lang === "en" ? "Different" : "مختلف" },
                ].map(({ color, bg, label }) => (
                  <div key={label} style={{ display: "flex", alignItems: "center", gap: ".5rem" }}>
                    <div style={{ width: 16, height: 16, borderRadius: 3, background: bg, border: `2px solid ${color}`, flexShrink: 0 }} />
                    <span style={{ fontSize: ".76rem", color: "var(--gray)", fontFamily: "var(--sans)" }}>{label}</span>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
